// The executive portal door — single source of truth for the leadership
// navbar (PJ Murphy, Ravi Joshi; kanban f9aec0be).
//
// The door is selected by the EXPLICIT `exec:portal` grant in the user's
// scopes list (app/auth/scopes.py Scope.EXEC_PORTAL), regardless of role: PJ
// and Ravi are ADMIN-role accounts today, and the admin `hasScope()` short-
// circuit must NOT turn every admin's portal into the executive one. See
// utils/landingPage.ts isExecPortalUser.
//
// Each tab below is a portal route whose page is the SAME component the admin
// panel serves (Roadmap, Quality Issues, Costs, …) rendered inside the portal
// shell, gated by ExecRoute in App.tsx. `route` and the backend guards MUST
// stay in step: every read endpoint behind a tab admits exec:portal
// (see scripts/grant_exec_portal_access.py for the full grant set).

export const EXEC_PORTAL_SCOPE = 'exec:portal';

/** Where an executive lands after login / on /portal. */
export const EXEC_PORTAL_HOME = '/portal/roadmap';

export type ExecTabId =
  | 'roadmap'
  | 'briefing'
  | 'signals'
  | 'quality-issues'
  | 'quality-copilot'
  | 'customer-grading'
  | 'costs'
  | 'portal-analytics'
  | 'knowledge-banks'
  | 'data-explorer';

export interface ExecTab {
  /** Stable id — matches portalNavUtils.getActiveTab for in-app routes. */
  id: ExecTabId;
  label: string;
  /** In-app portal path, or the Metabase pass-through for the external tab. */
  path: string;
  /** Opens in a new tab as a plain link (not a router navigation). */
  external?: boolean;
  /** Tooltip / a11y description. */
  title: string;
  /** Shown only when this scope is in the user's EXPLICIT scopes list (not
   *  the admin short-circuit): the tab is a per-person grant on top of the
   *  executive door. */
  requiresExplicitScope?: string;
}

// Registry order = navbar order. Roadmap first (the home), then the personal
// briefing Merlin writes for the reader, then the quality + business views.
// Labels are short on purpose: nine tabs plus the Merlin switcher must fit a
// 1280px laptop bar on one line (the full name lives in `title`).
export const EXEC_TABS: readonly ExecTab[] = [
  {
    id: 'roadmap',
    label: 'Roadmap',
    path: '/portal/roadmap',
    title: 'Initiative Roadmap — table, timeline, mind map, blocked, Merlin’s questions',
  },
  {
    id: 'briefing',
    label: 'Briefing',
    path: '/portal/signals/digest',
    // The reader's Merlin watches, their daily briefing first (kanban
    // 001b6e2b folded the personal digest into watches), and the chat that
    // changes them.
    title: 'Your daily briefing and your Merlin watches — tell Merlin what to watch and how you like to read',
  },
  {
    id: 'signals',
    label: 'Quality Signals',
    path: '/portal/signals',
    title: 'Quality Signals — reports feed and the Constellations explorer',
  },
  {
    id: 'quality-issues',
    label: 'Quality Issues',
    path: '/portal/quality-issues',
    title: 'Quality issues — every tracked issue and its history',
  },
  {
    // The read-only copy of Jess's Quality desk (kanban 808dabf6): cases by
    // priority, whose court, the Sigma clocks, the steps and the open asks,
    // plus Merlin to ask — nothing editable. Lit by the explicit
    // quality:viewer grant scripts/grant_quality_viewer_access.py adds.
    id: 'quality-copilot',
    label: 'Quality desk',
    path: '/portal/quality-copilot',
    title: 'Quality desk — the cases, threads, clocks and asks the quality team works, read-only',
    requiresExplicitScope: 'quality:viewer',
  },
  {
    id: 'customer-grading',
    label: 'Grading',
    path: '/portal/customer-grading',
    title: 'Customer A–F grading — runs and changes pending review',
  },
  {
    id: 'costs',
    label: 'Costs',
    path: '/portal/costs',
    title: 'Infrastructure and AI spend at a glance',
  },
  {
    id: 'portal-analytics',
    label: 'Analytics',
    path: '/portal/portal-analytics',
    title: 'Who is using the portal, and how',
  },
  {
    // The Knowledge Bank Explorer (kanban 4baf12cb): the gurucloud-kb SDK UI
    // hosted at /kb-explorer/, framed by pages/portal/PortalKnowledgeBanksPage.
    // Admin ruling 2026-09-21: "Ravi and PJ should be able to see it". The
    // backend gates it on kb:explore, which grant_exec_portal_access.py adds.
    id: 'knowledge-banks',
    label: 'Knowledge',
    path: '/portal/knowledge-banks',
    title: 'Knowledge Banks — browse, search, map and edit what the agents know',
  },
  {
    id: 'data-explorer',
    label: 'Data Explorer',
    path: '/metabase/',
    external: true,
    title: 'Metabase — explore the data directly (opens in a new tab)',
  },
] as const;

/** True when the path belongs to one of the in-app executive tabs. */
export function isExecTabPath(pathname: string): boolean {
  return EXEC_TABS.some(
    (t) => !t.external && (pathname === t.path || pathname.startsWith(t.path + '/')),
  );
}

/** The executive tabs this person sees: every registry tab, minus those whose
 *  explicit per-person grant is not in their scopes list. */
export function execTabsFor(explicitScopes: readonly string[] | null | undefined): ExecTab[] {
  const scopes = Array.isArray(explicitScopes) ? explicitScopes : [];
  return EXEC_TABS.filter(
    (t) => t.requiresExplicitScope === undefined || scopes.includes(t.requiresExplicitScope),
  );
}
